import React, { useCallback, useEffect, useRef, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { getCurrentUser } from "../../services/auth_service";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import { isSuperadmin, hasPermission } from "../utils/permissions";
import {
  uploadFleetFile,
  downloadFleetFile,
  fetchFleetList,
  fetchFleetSummary,
  updateFleetRecord,
  deleteFleetRecord,
} from "../../services/fleet_by_branch";
import "./FleetByBranchManager.css";

const STATUS = { IDLE: "idle", UPLOADING: "uploading", SUCCESS: "success", ERROR: "error" };
const PAGE_SIZE = 25;
const TOAST_AUTO_DISMISS_MS = 3200;

const EDIT_FIELDS = [
  { key: "registration_number", label: "Registration No", type: "text", required: true },
  { key: "ownership_type", label: "Ownership", type: "text", required: true },
  { key: "body_type", label: "Body Type", type: "text", required: true },
  { key: "vehicle_classification", label: "Vehicle Classification", type: "text", required: true },
  { key: "fuel_type", label: "Fuel Type", type: "text", required: true },
  { key: "operation_mode", label: "Operation Mode", type: "text", required: true },
  { key: "ntt", label: "Entity", type: "text", required: false },
  { key: "moy", label: "Year", type: "text", required: false, pattern: "\\d{4}", maxLength: 4 },
  { key: "dco_vdp_name", label: "DCO/VDP Name", type: "text", required: false },
  { key: "contact_no", label: "Contact No", type: "text", required: false },
  { key: "location", label: "Location", type: "text", required: false },
  { key: "details_shared_by", label: "Details Shared By", type: "text", required: false },
  { key: "remarks", label: "Remarks", type: "text", required: false },
];

const VIEW_FIELDS = [
  { key: "id", label: "ID" },
  { key: "transaction_id", label: "Transaction ID" },
  { key: "serial_no", label: "Serial No" },
  { key: "ntt", label: "Entity" },
  { key: "registration_number", label: "Registration No" },
  { key: "ownership_type", label: "Ownership" },
  { key: "body_type", label: "Body Type" },
  { key: "vehicle_classification", label: "Vehicle Classification" },
  { key: "fuel_type", label: "Fuel Type" },
  { key: "moy", label: "Year" },
  { key: "operation_mode", label: "Operation Mode" },
  { key: "dco_vdp_name", label: "DCO/VDP Name" },
  { key: "contact_no", label: "Contact No" },
  { key: "location", label: "Location" },
  { key: "details_shared_by", label: "Details Shared By" },
  { key: "remarks", label: "Remarks" },
  { key: "created_by", label: "Created By" },
  { key: "updated_by", label: "Updated By" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

const DATE_FIELDS = new Set(["created_at", "updated_at"]);

const VIEW_HERO_KEYS = ["transaction_id", "registration_number", "ownership_type", "fuel_type", "operation_mode"];

const formatViewValue = (key, rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return "—";
  if (DATE_FIELDS.has(key)) {
    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) return String(rawValue);
    return parsed.toLocaleString();
  }
  return String(rawValue);
};

const buildEditFormFromRecord = (record) => {
  const form = {};
  EDIT_FIELDS.forEach(({ key }) => {
    const value = record[key];
    form[key] = value === null || value === undefined ? "" : String(value);
  });
  return form;
};

const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function FleetByBranchManager() {
  const currentUser = getCurrentUser() || {};
  const confirm = useConfirm();

  const isSuperadminUser = isSuperadmin();
  const canViewFleet =
    isSuperadminUser || hasPermission("branch_overview", "view");
  const canEditFleet =
    isSuperadminUser || hasPermission("branch_overview", "edit");
  const canDeleteFleet =
    isSuperadminUser || hasPermission("branch_overview", "delete");

  const hasFleetAccess =
    isSuperadminUser ||
    canViewFleet ||
    canEditFleet ||
    canDeleteFleet;

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingTable, setLoadingTable] = useState(true);
  const [tableError, setTableError] = useState("");

  const [summary, setSummary] = useState({
    total: 0,
    ev: 0,
    ice: 0,
    own_fleet: 0,
    vdp: 0,
    dco: 0,
    sedan: 0,
    suv: 0,
  });

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ownershipType, setOwnershipType] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [bodyTypeFilter, setBodyTypeFilter] = useState("");
  const [fleetSourceFilter, setFleetSourceFilter] = useState("");
  const [vehicleClassificationFilter, setVehicleClassificationFilter] = useState("");
  const [detailsSharedByFilter, setDetailsSharedByFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [uploadedDates, setUploadedDates] = useState(new Set());
  const calendarRef = useRef(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [resultMessage, setResultMessage] = useState("");
  const [rowErrors, setRowErrors] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);

  const [openActionsId, setOpenActionsId] = useState(null);
  const actionsRef = useRef(null);

  const [viewingRecord, setViewingRecord] = useState(null);

  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [deleteBusyId, setDeleteBusyId] = useState(null);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = (type, text) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, text });
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
  };

  const dismissToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const loadUploadedDates = useCallback(async () => {
    try {
      let currentPage = 1;
      let totalRecords = 0;
      const dates = new Set();

      while (currentPage <= 100) {
        const data = await fetchFleetList({
          page: currentPage,
          page_size: 100,
        });

        const items = Array.isArray(data?.items) ? data.items : [];
        totalRecords = Number(data?.total || 0);

        items.forEach((item) => {
          if (!item?.created_at) return;
          const parsed = new Date(item.created_at);
          if (Number.isNaN(parsed.getTime())) return;
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          dates.add(`${year}-${month}-${day}`);
        });

        if (!items.length || currentPage * 100 >= totalRecords) break;
        currentPage += 1;
      }

      setUploadedDates(dates);
    } catch {
      setUploadedDates(new Set());
    }
  }, []);

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i += 1) days.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(new Date(year, month, day));
    return days;
  };

  const formatCalendarDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatCalendarTitle = () =>
    calendarMonth.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

  const goToPreviousMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };

  const selectCalendarDate = (date) => {
    if (!date) return;
    setSelectedDate(formatCalendarDate(date));
    setCalendarOpen(false);
    setPage(1);
  };

  const goToToday = () => {
    const today = new Date();
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(formatCalendarDate(today));
    setCalendarOpen(false);
    setPage(1);
  };

  const clearSelectedDate = () => {
    setSelectedDate("");
    setCalendarOpen(false);
    setPage(1);
  };

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchFleetSummary({
        search: search || undefined,
        ownership_type: ownershipType || undefined,
        fuel_type: fuelType || undefined,
        location: locationFilter || undefined,
        body_type: bodyTypeFilter || undefined,
        fleet_source: fleetSourceFilter || undefined,
        vehicle_classification: vehicleClassificationFilter || undefined,
        details_shared_by: detailsSharedByFilter || undefined,
        year: yearFilter ? Number(yearFilter) : undefined,
        date: selectedDate || undefined,
      });
      setSummary(data);
    } catch {
      setSummary((s) => s);
    }
  }, [
    search,
    ownershipType,
    fuelType,
    locationFilter,
    bodyTypeFilter,
    fleetSourceFilter,
    vehicleClassificationFilter,
    detailsSharedByFilter,
    yearFilter,
    selectedDate,
  ]);

  const loadRecords = useCallback(async () => {
    setLoadingTable(true);
    setTableError("");
    try {
      const data = await fetchFleetList({
        search: search || undefined,
        ownership_type: ownershipType || undefined,
        fuel_type: fuelType || undefined,
        location: locationFilter || undefined,
        body_type: bodyTypeFilter || undefined,
        fleet_source: fleetSourceFilter || undefined,
        vehicle_classification: vehicleClassificationFilter || undefined,
        details_shared_by: detailsSharedByFilter || undefined,
        year: yearFilter ? Number(yearFilter) : undefined,
        date: selectedDate || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setRecords(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setTableError(err.message || "Could not load fleet records.");
      setRecords([]);
      setTotal(0);
    } finally {
      setLoadingTable(false);
    }
  }, [
    search,
    ownershipType,
    fuelType,
    locationFilter,
    bodyTypeFilter,
    fleetSourceFilter,
    vehicleClassificationFilter,
    detailsSharedByFilter,
    yearFilter,
    selectedDate,
    page,
  ]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadUploadedDates();
  }, [loadUploadedDates]);

  useEffect(() => {
    if (!calendarOpen) return;

    const handleCalendarOutsideClick = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleCalendarOutsideClick);
    return () => document.removeEventListener("mousedown", handleCalendarOutsideClick);
  }, [calendarOpen]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (openActionsId === null) return;
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setOpenActionsId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionsId]);

  const resetUploadState = () => {
    setStatus(STATUS.IDLE);
    setProgress(0);
    setResultMessage("");
    setRowErrors([]);
    setFileName(null);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    resetUploadState();
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    if (!isExcel) {
      setFileName(file.name);
      setStatus(STATUS.ERROR);
      setResultMessage("Only .xlsx or .xls files are accepted.");
      setRowErrors([]);
      return;
    }
    setFileName(file.name);
    setStatus(STATUS.UPLOADING);
    setProgress(0);
    setResultMessage("");
    setRowErrors([]);
    try {
      const result = await uploadFleetFile(file, setProgress);
      setStatus(STATUS.SUCCESS);
      setResultMessage(`${result.rows_inserted} row${result.rows_inserted === 1 ? "" : "s"} inserted.`);
      setUploadedDates((current) => {
        const next = new Set(current);
        next.add(selectedDate || getTodayDateString());
        return next;
      });
      loadRecords();
      loadSummary();
      loadUploadedDates();
    } catch (err) {
      setStatus(STATUS.ERROR);
      setResultMessage(err.message || "Upload failed.");
      setRowErrors(err.errors || []);
    }
  }, [loadRecords, loadSummary, loadUploadedDates, selectedDate]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, [handleFile]);

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const onBrowseClick = () => inputRef.current?.click();

  const onInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadFleetFile({
        search: search || undefined,
        ownership_type: ownershipType || undefined,
        fuel_type: fuelType || undefined,
        location: locationFilter || undefined,
        body_type: bodyTypeFilter || undefined,
        fleet_source: fleetSourceFilter || undefined,
        vehicle_classification: vehicleClassificationFilter || undefined,
        details_shared_by: detailsSharedByFilter || undefined,
        year: yearFilter ? Number(yearFilter) : undefined,
        date: selectedDate || undefined,
      });
    } catch (err) {
      setTableError(err.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    setOwnershipType("");
    setFuelType("");
    setLocationFilter("");
    setBodyTypeFilter("");
    setFleetSourceFilter("");
    setVehicleClassificationFilter("");
    setDetailsSharedByFilter("");
    setYearFilter("");
    setPage(1);
  };

  const onDateChange = (e) => {
    const value = e.target.value;
    setSelectedDate(value);
    setPage(1);

    if (value) {
      const [year, month] = value.split("-").map(Number);
      setCalendarMonth(new Date(year, month - 1, 1));
    }
  };


  const toggleActions = (recordId) => {
    setOpenActionsId((current) => (current === recordId ? null : recordId));
  };

  const openViewModal = (record) => {
    setOpenActionsId(null);
    setViewingRecord(record);
  };

  const closeViewModal = () => {
    setViewingRecord(null);
  };

  const switchToEditFromView = () => {
    if (!canEditFleet) return;
    if (!viewingRecord) return;
    const record = viewingRecord;
    setViewingRecord(null);
    openEditForm(record);
  };

  const openEditForm = (record) => {
    if (!canEditFleet) return;
    setOpenActionsId(null);
    setEditError("");
    setEditingRecord(record);
    setEditForm(buildEditFormFromRecord(record));
  };

  const closeEditForm = () => {
    setEditingRecord(null);
    setEditForm({});
    setEditError("");
    setEditSaving(false);
  };

  const onEditFieldChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const onEditSave = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;

    setEditError("");

    const missingRequired = EDIT_FIELDS.filter(
      (f) => f.required && !String(editForm[f.key] ?? "").trim()
    );
    if (missingRequired.length > 0) {
      setEditError(`${missingRequired[0].label} is required.`);
      return;
    }

    const yearValue = String(editForm.moy ?? "").trim();
    if (yearValue && !/^\d{4}$/.test(yearValue)) {
      setEditError("Year must be a 4-digit number (e.g. 2024).");
      return;
    }

    const payload = {};
    EDIT_FIELDS.forEach(({ key, type }) => {
      const raw = editForm[key];
      const trimmed = typeof raw === "string" ? raw.trim() : raw;
      if (type === "number") {
        payload[key] = trimmed === "" ? null : Number(trimmed);
      } else {
        payload[key] = trimmed === "" ? null : trimmed;
      }
    });
    payload.serial_no = Number(editingRecord.serial_no);

    setEditSaving(true);
    try {
      await updateFleetRecord(editingRecord.id, payload);
      closeEditForm();
      loadRecords();
      loadSummary();
      showToast("success", "Vehicle record updated successfully");
    } catch (err) {
      setEditError(err.message || "Could not save changes.");
      setEditSaving(false);
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!canDeleteFleet) return;
    setOpenActionsId(null);

    const confirmed = await confirm({
      title: "Delete this record?",
      message: `This will permanently delete the record for ${record.registration_number || "this vehicle"}. This action cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
    });
    if (!confirmed) return;

    setDeleteBusyId(record.id);
    try {
      await deleteFleetRecord(record.id);
      loadRecords();
      loadSummary();
      showToast("success", "Vehicle record deleted successfully");
    } catch (err) {
      showToast("error", err.message || "Could not delete record.");
    } finally {
      setDeleteBusyId(null);
    }
  };

  const hasActiveFilters =
    ownershipType ||
    fuelType ||
    locationFilter ||
    bodyTypeFilter ||
    fleetSourceFilter ||
    vehicleClassificationFilter ||
    detailsSharedByFilter ||
    yearFilter;

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  if (!hasFleetAccess) {
    return (
      <div className="fleet-page">
        <Sidebar />
        <div className="fleet-page__main">
          <Header title="Fleet By Branch" />
          <div className="fleet-content">
            <div className="fleet-table-banner fleet-table-banner--error">
              You don't have permission to view Fleet By Branch.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-page">
      <Sidebar />
      <div className="fleet-page__main">
        <Header title="Fleet By Branch" />

        <div className="fleet-content">
          <div className="fleet-stats-groups">
            <div className="fleet-stats">
              <div className="fleet-stat fleet-stat--blue">
                <span className="fleet-stat__value">{summary.total}</span>
                <span className="fleet-stat__label">Total vehicles</span>
              </div>
            </div>

            <div className="fleet-stats-group">
              <span className="fleet-stats-group__title">Fleet source</span>
              <div className="fleet-stats">
                <div className="fleet-stat fleet-stat--purple">
                  <span className="fleet-stat__value">{summary.own_fleet}</span>
                  <span className="fleet-stat__label">Own Fleet</span>
                </div>
                <div className="fleet-stat fleet-stat--indigo">
                  <span className="fleet-stat__value">{summary.vdp}</span>
                  <span className="fleet-stat__label">VDP</span>
                </div>
                <div className="fleet-stat fleet-stat--slate">
                  <span className="fleet-stat__value">{summary.dco}</span>
                  <span className="fleet-stat__label">DCO</span>
                </div>
              </div>
            </div>

            <div className="fleet-stats-group">
              <span className="fleet-stats-group__title">Fuel type</span>
              <div className="fleet-stats">
                <div className="fleet-stat fleet-stat--teal">
                  <span className="fleet-stat__value">{summary.ev}</span>
                  <span className="fleet-stat__label">EV cabs</span>
                </div>
                <div className="fleet-stat fleet-stat--amber">
                  <span className="fleet-stat__value">{summary.ice}</span>
                  <span className="fleet-stat__label">ICE cabs</span>
                </div>
              </div>
            </div>

            <div className="fleet-stats-group">
              <span className="fleet-stats-group__title">Vehicle type</span>
              <div className="fleet-stats">
                <div className="fleet-stat fleet-stat--green">
                  <span className="fleet-stat__value">{summary.sedan}</span>
                  <span className="fleet-stat__label">Sedan</span>
                </div>
                <div className="fleet-stat fleet-stat--red">
                  <span className="fleet-stat__value">{summary.suv}</span>
                  <span className="fleet-stat__label">SUV</span>
                </div>
              </div>
            </div>
          </div>

          <div className="fleet-toolbar">
            <div className="fleet-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search registration no, Entity, transaction id, location, DCO/VDP name, contact no, remarks..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="fleet-toolbar__right">
              <div className="fleet-date-picker" ref={calendarRef}>
                <button
                  type="button"
                  className={`fleet-date-picker__trigger ${calendarOpen ? "fleet-date-picker__trigger--open" : ""}`}
                  onClick={() => {
                    if (!calendarOpen && selectedDate) {
                      const [year, month] = selectedDate.split("-").map(Number);
                      setCalendarMonth(new Date(year, month - 1, 1));
                    }
                    setCalendarOpen((value) => !value);
                  }}
                >
                  <span className="fleet-date-picker__value">
                    {selectedDate
                      ? (() => {
                          const [year, month, day] = selectedDate.split("-").map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          });
                        })()
                      : "Select date"}
                  </span>
                  <span className="fleet-date-picker__calendar-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </button>

                {calendarOpen && (
                  <div className="fleet-calendar">
                    <div className="fleet-calendar__header">
                      <button type="button" className="fleet-calendar__nav" onClick={goToPreviousMonth} aria-label="Previous month">‹</button>
                      <button type="button" className="fleet-calendar__month" onClick={goToToday}>{formatCalendarTitle()}</button>
                      <button type="button" className="fleet-calendar__nav" onClick={goToNextMonth} aria-label="Next month">›</button>
                    </div>

                    <div className="fleet-calendar__weekdays">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => <span key={day}>{day}</span>)}
                    </div>

                    <div className="fleet-calendar__grid">
                      {getCalendarDays().map((date, index) => {
                        if (!date) return <span key={`empty-${index}`} className="fleet-calendar__empty" />;

                        const value = formatCalendarDate(date);
                        const isSelected = selectedDate === value;
                        const hasUpload = uploadedDates.has(value);
                        const isToday = getTodayDateString() === value;

                        return (
                          <button
                            key={value}
                            type="button"
                            className={[
                              "fleet-calendar__day",
                              isSelected ? "fleet-calendar__day--selected" : "",
                              isToday ? "fleet-calendar__day--today" : "",
                              hasUpload ? "fleet-calendar__day--has-upload" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => selectCalendarDate(date)}
                          >
                            <span>{date.getDate()}</span>
                            {hasUpload && <span className="fleet-calendar__upload-dot" title="Data uploaded" />}
                          </button>
                        );
                      })}
                    </div>

                    <div className="fleet-calendar__footer">
                      <button type="button" onClick={clearSelectedDate}>Clear</button>
                      <button type="button" onClick={goToToday}>Today</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="fleet-filters-wrap">
                <button
                  type="button"
                  className={`fleet-btn fleet-btn--outline ${hasActiveFilters ? "fleet-btn--filters-active" : ""}`}
                  onClick={() => setFiltersOpen((v) => !v)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="7" y1="12" x2="17" y2="12" />
                    <line x1="10" y1="18" x2="14" y2="18" />
                  </svg>
                  Filters
                  {hasActiveFilters && <span className="fleet-btn__dot" />}
                </button>

                {filtersOpen && (
                  <>
                    <div className="fleet-filters-backdrop" onClick={() => setFiltersOpen(false)} />
                    <div className="fleet-filters-panel">
                      <div className="fleet-filters-panel__header">
                        <span className="fleet-filters-panel__title">Filters</span>
                        <button
                          type="button"
                          className="fleet-modal__close"
                          onClick={() => setFiltersOpen(false)}
                          aria-label="Close filters"
                        >
                          ×
                        </button>
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Ownership</label>
                        <select value={ownershipType} onChange={(e) => { setOwnershipType(e.target.value); setPage(1); }}>
                          <option value="">All</option>
                          <option value="COMMERCIAL">Commercial</option>
                          <option value="PRIVATE">Private</option>
                        </select>
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Fuel type</label>
                        <select value={fuelType} onChange={(e) => { setFuelType(e.target.value); setPage(1); }}>
                          <option value="">All</option>
                          <option value="EV">EV</option>
                          <option value="ICE">ICE</option>
                        </select>
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Body type</label>
                        <select value={bodyTypeFilter} onChange={(e) => { setBodyTypeFilter(e.target.value); setPage(1); }}>
                          <option value="">All</option>
                          <option value="SEDAN">Sedan</option>
                          <option value="SUV">SUV</option>
                        </select>
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Fleet source</label>
                        <select value={fleetSourceFilter} onChange={(e) => { setFleetSourceFilter(e.target.value); setPage(1); }}>
                          <option value="">All</option>
                          <option value="OWN_FLEET">Own Fleet</option>
                          <option value="VDP">VDP</option>
                          <option value="DCO">DCO</option>
                        </select>
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Vehicle makes</label>
                        <input
                          type="text"
                          placeholder="All"
                          value={vehicleClassificationFilter}
                          onChange={(e) => { setVehicleClassificationFilter(e.target.value); setPage(1); }}
                        />
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Location</label>
                        <input
                          type="text"
                          placeholder="All"
                          value={locationFilter}
                          onChange={(e) => { setLocationFilter(e.target.value); setPage(1); }}
                        />
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Details shared by</label>
                        <input
                          type="text"
                          placeholder="All"
                          value={detailsSharedByFilter}
                          onChange={(e) => { setDetailsSharedByFilter(e.target.value); setPage(1); }}
                        />
                      </div>

                      <div className="fleet-filters-panel__field">
                        <label>Year</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="e.g. 2024"
                          value={yearFilter}
                          onChange={(e) => { setYearFilter(e.target.value.replace(/\D/g, "").slice(0, 4)); setPage(1); }}
                        />
                      </div>

                      {hasActiveFilters && (
                        <button type="button" className="fleet-filters-panel__clear" onClick={clearFilters}>
                          Clear filters
                        </button>
                      )}

                      <button
                        type="button"
                        className="fleet-btn fleet-btn--solid filter-done-btn"
                        onClick={() => setFiltersOpen(false)}
                      >
                        Done
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button type="button" className="fleet-btn fleet-btn--outline" onClick={onDownload} disabled={downloading}>
                {downloading ? "Preparing…" : "Download"}
              </button>

              {hasFleetAccess && (
                <button type="button" className="fleet-btn fleet-btn--solid" onClick={() => setUploadModalOpen(true)}>
                  Bulk upload
                </button>
              )}
            </div>
          </div>

          <div className="fleet-table-wrap">
            {tableError && <div className="fleet-table-banner fleet-table-banner--error">{tableError}</div>}

            <div className="fleet-table-scroll">
              <table className="fleet-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Entity</th>
                    <th>Registration No</th>
                    <th>Ownership</th>
                    <th>Body Type</th>
                    <th>Vehicle Classification</th>
                    <th>Fuel</th>
                    <th>Year</th>
                    <th>Operation Mode</th>
                    <th>DCO/VDP Name</th>
                    <th>Contact No</th>
                    <th>Location</th>
                    <th>Details Shared By</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTable && (
                    <tr><td colSpan={20} className="fleet-table__empty">Loading records…</td></tr>
                  )}
                  {!loadingTable && records.length === 0 && (
                    <tr>
                      <td colSpan={20} className="fleet-table__empty">
                        {selectedDate
                          ? `No records found for ${selectedDate}.`
                          : yearFilter
                            ? `No records found for year ${yearFilter}.`
                            : "No records found."}
                      </td>
                    </tr>
                  )}
                  {!loadingTable && records.map((r) => (
                    <tr key={r.id}>
                      <td><span className="fleet-pill">{r.transaction_id}</span></td>
                      <td className="fleet-mono">{r.ntt || "—"}</td>
                      <td className="fleet-mono">{r.registration_number}</td>
                      <td>{r.ownership_type}</td>
                      <td>{r.body_type}</td>
                      <td>{r.vehicle_classification}</td>
                      <td>{r.fuel_type}</td>
                      <td>{r.moy || "—"}</td>
                      <td>{r.operation_mode}</td>
                      <td>{r.dco_vdp_name || "—"}</td>
                      <td className="fleet-mono">{r.contact_no || "—"}</td>
                      <td>{r.location || "—"}</td>
                      <td>{r.details_shared_by || "—"}</td>
                      <td>{r.remarks || "—"}</td>

                      <td className="fleet-actions-cell">
                        <div
                          className="fleet-actions"
                          ref={openActionsId === r.id ? actionsRef : null}
                        >
                          <button
                            type="button"
                            className="fleet-actions__trigger"
                            onClick={() => toggleActions(r.id)}
                          >
                            Actions
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>

                          {openActionsId === r.id && (
                            <div className="fleet-actions__menu">
                              {canViewFleet && (
                                <button
                                  type="button"
                                  className="fleet-actions__item"
                                  onClick={() => openViewModal(r)}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                  View
                                </button>
                              )}

                              {canEditFleet && (
                                <button
                                  type="button"
                                  className="fleet-actions__item"
                                  onClick={() => openEditForm(r)}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                  </svg>
                                  Edit
                                </button>
                              )}

                              {canDeleteFleet && (
                                <button
                                  type="button"
                                  className="fleet-actions__item fleet-actions__item--danger"
                                  disabled={deleteBusyId === r.id}
                                  onClick={() => handleDeleteRecord(r)}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  </svg>
                                  {deleteBusyId === r.id ? "Deleting…" : "Delete"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fleet-table__footer">
              <span>
                Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of <strong>{total}</strong> records
              </span>
              <div className="fleet-pagination">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>‹</button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(p + 1, totalPages))}>›</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadModalOpen && (
        <div className="fleet-modal-overlay" onClick={closeUploadModal}>
          <div className="fleet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fleet-modal__header">
              <h2>Bulk upload</h2>
              <button type="button" className="fleet-modal__close" onClick={closeUploadModal} aria-label="Close">×</button>
            </div>

            <section
              className={[
                "fleet-dock",
                dragActive ? "fleet-dock--active" : "",
                status === STATUS.ERROR ? "fleet-dock--error" : "",
                status === STATUS.SUCCESS ? "fleet-dock--success" : "",
              ].join(" ").trim()}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
            >
              <input ref={inputRef} type="file" accept=".xlsx,.xls" className="fleet-dock__input" onChange={onInputChange} />

              {status === STATUS.IDLE && (
                <>
                  <p className="fleet-dock__label">Drag manifest file here</p>
                  <p className="fleet-dock__sub">.xlsx or .xls — matches the bulk upload template</p>
                  <button type="button" className="fleet-btn fleet-btn--solid" onClick={onBrowseClick}>Choose file</button>
                </>
              )}

              {status === STATUS.UPLOADING && (
                <div className="fleet-dock__progress-wrap">
                  <p className="fleet-dock__label">Reading {fileName}</p>
                  <div className="fleet-gauge"><div className="fleet-gauge__fill" style={{ width: `${progress}%` }} /></div>
                  <p className="fleet-dock__sub fleet-mono">{progress}%</p>
                </div>
              )}

              {status === STATUS.SUCCESS && (
                <>
                  <p className="fleet-dock__label fleet-dock__label--success">Manifest accepted</p>
                  <p className="fleet-dock__sub">{fileName}</p>
                  <p className="fleet-dock__result">{resultMessage}</p>
                  <button type="button" className="fleet-btn fleet-btn--outline" onClick={resetUploadState}>Upload another file</button>
                </>
              )}

              {status === STATUS.ERROR && (
                <>
                  <p className="fleet-dock__label fleet-dock__label--error">Manifest rejected</p>
                  <p className="fleet-dock__sub">{fileName}</p>
                  <p className="fleet-dock__result fleet-dock__result--error">{resultMessage}</p>
                  <button type="button" className="fleet-btn fleet-btn--outline" onClick={resetUploadState}>Try again</button>
                </>
              )}
            </section>

            {rowErrors.length > 0 && (
              <section className="fleet-log">
                <p className="fleet-log__title">Rows that failed validation ({rowErrors.length})</p>
                <div className="fleet-log__table-wrap">
                  <table className="fleet-log__table">
                    <thead>
                      <tr><th>Row</th><th>Field</th><th>Issue</th></tr>
                    </thead>
                    <tbody>
                      {rowErrors.map((rowErr, i) =>
                        rowErr.errors.map((fieldErr, j) => (
                          <tr key={`${i}-${j}`}>
                            {j === 0 && <td rowSpan={rowErr.errors.length} className="fleet-mono">{rowErr.row}</td>}
                            <td className="fleet-mono">{Array.isArray(fieldErr.loc) ? fieldErr.loc.join(".") : String(fieldErr.loc)}</td>
                            <td>{fieldErr.msg}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {viewingRecord && (
        <div className="fleet-modal-overlay" onClick={closeViewModal}>
          <div className="fleet-modal fleet-modal--view" onClick={(e) => e.stopPropagation()}>
            <div className="fleet-view__hero">
              <div className="fleet-view__hero-top">
                <span className="fleet-pill fleet-pill--lg">{viewingRecord.transaction_id}</span>
              </div>
              <button type="button" className="fleet-modal__close" onClick={closeViewModal} aria-label="Close">×</button>
              <h2 className="fleet-view__hero-title">{viewingRecord.registration_number || "—"}</h2>
              <div className="fleet-view__hero-tags">
                {viewingRecord.ownership_type && (
                  <span className="fleet-tag">{viewingRecord.ownership_type}</span>
                )}
                {viewingRecord.fuel_type && (
                  <span className={`fleet-tag ${viewingRecord.fuel_type.toUpperCase() === "EV" ? "fleet-tag--teal" : "fleet-tag--amber"}`}>
                    {viewingRecord.fuel_type}
                  </span>
                )}
                {viewingRecord.operation_mode && (
                  <span className="fleet-tag fleet-tag--blue">{viewingRecord.operation_mode}</span>
                )}
              </div>
            </div>

            <div className="fleet-view">
              <dl className="fleet-view__grid">
                {VIEW_FIELDS.filter(({ key }) => !VIEW_HERO_KEYS.includes(key)).map(({ key, label }) => (
                  <div className="fleet-view__field" key={key}>
                    <dt>{label}</dt>
                    <dd>{formatViewValue(key, viewingRecord[key])}</dd>
                  </div>
                ))}
              </dl>

              <div className="fleet-edit-form__actions">
                {canEditFleet && (
                  <button type="button" className="fleet-btn fleet-btn--solid" onClick={switchToEditFromView}>
                    Edit this record
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {canEditFleet && editingRecord && (
        <div className="fleet-modal-overlay" onClick={closeEditForm}>
          <div className="fleet-modal fleet-modal--edit" onClick={(e) => e.stopPropagation()}>
            <div className="fleet-modal__header">
              <h2>Edit vehicle — {editingRecord.transaction_id}</h2>
              <button type="button" className="fleet-modal__close" onClick={closeEditForm} aria-label="Close">×</button>
            </div>

            <form className="fleet-edit-form" onSubmit={onEditSave}>
              <div className="fleet-edit-form__grid">
                {EDIT_FIELDS.map(({ key, label, type, required, pattern, maxLength }) => (
                  <div className="fleet-edit-form__field" key={key}>
                    <label htmlFor={`edit-${key}`}>
                      {label}
                      {required && <span className="fleet-edit-form__required"> *</span>}
                    </label>
                    <input
                      id={`edit-${key}`}
                      type={type}
                      value={editForm[key] ?? ""}
                      onChange={(e) => onEditFieldChange(key, e.target.value)}
                      required={required}
                      pattern={pattern}
                      maxLength={maxLength}
                    />
                  </div>
                ))}
              </div>

              {editError && <p className="fleet-edit-form__error">{editError}</p>}

              <div className="fleet-edit-form__actions">
                <button type="button" className="fleet-btn fleet-btn--outline" onClick={closeEditForm} disabled={editSaving}>
                  Cancel
                </button>
                <button type="submit" className="fleet-btn fleet-btn--solid" disabled={editSaving}>
                  {editSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fleet-toast-overlay"
          style={{
            position: "fixed",
            bottom: "28px",
            right: "28px",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "14px 18px",
            borderRadius: "10px",
            minWidth: "260px",
            maxWidth: "380px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 500,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
            animation: "fleetToastSlideIn 0.25s ease-out",
          }}
        >
          <span style={{ fontSize: "16px", lineHeight: 1 }}>
            {toast.type === "success" ? "✓" : "⚠"}
          </span>
          <span style={{ flex: 1 }}>{toast.text}</span>
          <button
            type="button"
            onClick={dismissToast}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              opacity: 0.85,
              cursor: "pointer",
              fontSize: "15px",
              lineHeight: 1,
              padding: "2px",
            }}
          >
            ✕
          </button>
        </div>
      )}
      <style>{`
        @keyframes fleetToastSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}